import { useNavigate } from "react-router-dom";
import { XStack, YStack } from "tamagui";

import BetaJoinCard from "@/components/BetaJoinCard";
import AppStoreBadge from "@/components/AppStoreBadge";
import KindredSocialLinksCard from "@/components/KindredSocialLinksCard";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppContentContainer,
  AppHeading,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";
import {
  PublicBrandCard,
  PublicMarketingFooter,
  PublicMarketingPage,
} from "@/components/PublicMarketingScaffold";
import {
  ADULTING_PUBLIC_URL,
  STEADY_HOUSE_FOUNDER_NAME,
  STEADY_HOUSE_LEGAL_NAME,
  STEADY_HOUSE_WEBSITE_URL,
  getKindredPublicTagline,
  getKindredSupportFeatureDescription,
} from "../../shared/support";

const FEATURE_CARDS = [
  {
    emoji: "📰",
    name: "Kinfeed",
    description: "The warm front door to new conversation, nearby families, and easy ways to join in",
  },
  {
    emoji: "💬",
    name: "Community",
    description: "Channels, direct messages, and member spaces that keep your village organised",
  },
  {
    emoji: "📋",
    name: "Support",
    description: getKindredSupportFeatureDescription(),
  },
  {
    emoji: "📅",
    name: "Activities",
    description: "Group plans and What’s On listings in one active place",
  },
];

const DIFFERENTIATORS = [
  { title: "No paywall. Ever.", description: "Everything is free. No premium tier, no locked features." },
  { title: "No dating mode. Not a hint of it.", description: "This is a family community. Period." },
  {
    title: "Built by a single parent, for single parent families.",
    description: "Not by a corporation. By someone who lives this life.",
  },
];

function openExternal(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

const Landing = () => {
  const navigate = useNavigate();

  return (
    <PublicMarketingPage>
      <section className="bg-background py-12 text-center">
        <AppContentContainer>
          <AppCard position="relative" overflow="hidden" backgroundColor="$canvasMuted" borderColor="$borderColor" borderRadius="$xl">
            <YStack
              pointerEvents="none"
              position="absolute"
              top={-46}
              right={-18}
              width={160}
              height={160}
              borderRadius="$pill"
              backgroundColor="$secondarySoft"
            />
            <YStack
              pointerEvents="none"
              position="absolute"
              bottom={-64}
              left={-18}
              width={172}
              height={172}
              borderRadius="$pill"
              style={{ backgroundColor: "rgba(107, 53, 102, 0.16)" }}
            />
            <AppCardBody alignItems="center" px="$xl" py="$xl" gap="$lg">
              <PublicBrandCard />
              <YStack alignItems="center" gap="$sm" maxWidth={640}>
                <AppText
                  textAlign="center"
                  fontFamily={kindredFontFamilies.sansBold}
                  fontSize="$xs"
                  lineHeight="$sm"
                  color="$primary"
                  textTransform="uppercase"
                  letterSpacing={1.4}
                >
                  {getKindredPublicTagline()}
                </AppText>
                <AppHeading
                  textAlign="center"
                  fontSize="$hero"
                  lineHeight="$hero"
                  letterSpacing="$hero"
                  $gtSm={{ fontSize: "$heroLg", lineHeight: "$heroLg", letterSpacing: "$heroLg" }}
                >
                  Find your people. Plan activities. Get the support you need.
                </AppHeading>
                <AppText textAlign="center" fontSize="$lg" lineHeight="$lg" maxWidth={520} color="$colorMuted">
                  The free community for single mums, single dads, and solo parent families — no paywall, no dating
                  mode, and an honest country-by-country support rollout.
                </AppText>
              </YStack>
              <AppStoreBadge loading="eager" />
              <YStack width="100%" maxWidth={320} gap="$sm">
                <AppButton onPress={() => navigate("/signup")} size="lg">
                  Join Kindred — it&apos;s free
                </AppButton>
                <AppButton
                  tone="outline"
                  onPress={() => navigate("/signin")}
                  size="lg"
                  backgroundColor="$cardBackground"
                  borderColor="$borderStrong"
                  color="$primary"
                >
                  Sign in
                </AppButton>
              </YStack>
              <XStack gap="$sm">
                <YStack width={12} height={12} borderRadius="$pill" backgroundColor="$accent" opacity={0.8} />
                <YStack width={12} height={12} borderRadius="$pill" backgroundColor="$secondary" opacity={0.4} />
                <YStack width={12} height={12} borderRadius="$pill" backgroundColor="$accent" opacity={0.8} />
              </XStack>
            </AppCardBody>
          </AppCard>
        </AppContentContainer>
      </section>

      <section className="bg-card py-14">
        <AppContentContainer>
          <YStack gap="$lg">
            <AppHeading textAlign="center" fontSize="$xxl" lineHeight="$xxl" letterSpacing="$xxl">
              Everything a single parent family needs, in one place
            </AppHeading>
            <div className="grid grid-cols-2 gap-4">
              {FEATURE_CARDS.map((feature) => (
                <AppCard key={feature.name} backgroundColor="$background">
                  <AppCardBody alignItems="center" py="$md" gap="$xs">
                    <span className="text-[40px] leading-none">{feature.emoji}</span>
                    <AppText
                      textAlign="center"
                      fontFamily={kindredFontFamilies.sansBold}
                      fontSize="$md"
                      lineHeight="$md"
                      color="$primary"
                    >
                      {feature.name}
                    </AppText>
                    <AppText textAlign="center" fontSize="$xs" lineHeight="$sm" color="$colorMuted">
                      {feature.description}
                    </AppText>
                  </AppCardBody>
                </AppCard>
              ))}
            </div>
          </YStack>
        </AppContentContainer>
      </section>

      <section className="kindred-plum-surface py-14">
        <AppContentContainer>
          <YStack gap="$lg">
            {DIFFERENTIATORS.map((item) => (
              <YStack key={item.title} alignItems="center" gap="$xs">
                <AppText
                  textAlign="center"
                  fontFamily={kindredFontFamilies.sansBold}
                  fontSize="$xl"
                  lineHeight="$xl"
                  color="$primary"
                >
                  {item.title}
                </AppText>
                <AppText textAlign="center" fontSize="$sm" lineHeight="$md" color="$colorMuted" maxWidth={560}>
                  {item.description}
                </AppText>
              </YStack>
            ))}
          </YStack>
        </AppContentContainer>
      </section>

      <section className="bg-background py-14">
        <AppContentContainer>
          <AppCard backgroundColor="$cardBackground">
            <AppCardBody alignItems="center" py="$xl" gap="$sm">
              <AppText
                textAlign="center"
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$xs"
                lineHeight="$sm"
                color="$primary"
                textTransform="uppercase"
                letterSpacing={1.4}
              >
                Why Hannah built Kindred
              </AppText>
              <AppText textAlign="center" fontSize="$lg" lineHeight="$xl" maxWidth={700}>
                Single parent families should not have to choose between lonely generic social apps, paywalled
                communities, and spaces that quietly turn into dating platforms. Kindred is being built to make
                finding people, planning family-friendly things, and getting practical support feel clear, safe, and
                genuinely welcoming, while local guidance is rolled out carefully country by country.
              </AppText>
              <AppText textAlign="center" fontSize="$sm" lineHeight="$md" color="$colorMuted" maxWidth={620}>
                Founder-led by {STEADY_HOUSE_FOUNDER_NAME}, single parent of two. Operated by {STEADY_HOUSE_LEGAL_NAME}.
              </AppText>
              <XStack marginTop="$sm" gap="$sm" flexWrap="wrap" justifyContent="center">
                <AppButton tone="outline" onPress={() => navigate("/about-kindred")}>
                  About Kindred
                </AppButton>
              </XStack>
            </AppCardBody>
          </AppCard>
        </AppContentContainer>
      </section>

      <section className="bg-card py-14">
        <AppContentContainer>
          <BetaJoinCard id="android-beta-testing" />
        </AppContentContainer>
      </section>

      <section className="bg-background py-14">
        <AppContentContainer>
          <KindredSocialLinksCard
            sourceScreen="public_landing"
            helperText="Follow Kindred outside the app when you want launch updates or an easy link to share with another single parent."
          />
        </AppContentContainer>
      </section>

      <section className="bg-card py-14">
        <AppContentContainer>
          <AppCard backgroundColor="$cardBackground">
            <AppCardBody alignItems="center" py="$xl" gap="$sm">
              <AppText
                textAlign="center"
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$xs"
                lineHeight="$sm"
                color="$primary"
                textTransform="uppercase"
                letterSpacing={1.4}
              >
                From Hannah at Steady House
              </AppText>
              <AppText textAlign="center" fontSize="$lg" lineHeight="$xl" maxWidth={680}>
                Kindred is the community product. Adulting is the sister app for bills, renewals, documents, and the
                rest of life admin when the practical load is landing on you too.
              </AppText>
              <XStack marginTop="$sm" gap="$sm" flexWrap="wrap" justifyContent="center">
                <AppButton tone="outline" onPress={() => openExternal(STEADY_HOUSE_WEBSITE_URL)}>
                  Visit Steady House
                </AppButton>
                <AppButton tone="outline" onPress={() => openExternal(ADULTING_PUBLIC_URL)}>
                  Explore Adulting
                </AppButton>
              </XStack>
            </AppCardBody>
          </AppCard>
        </AppContentContainer>
      </section>

      <PublicMarketingFooter />
    </PublicMarketingPage>
  );
};

export default Landing;
